// src/components/StrategyBuilder/StepReview.jsx
import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  Divider,
  Alert,
  Button,
  CircularProgress,
  Paper,
  alpha,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  FlashOn as FlashIcon,
  CompareArrows as SequenceIcon,
  Block as InterferenceIcon,
  Description as DescIcon,
  TrendingUp as LongIcon,
  TrendingDown as ShortIcon,
} from '@mui/icons-material';
import { validateStrategy } from '../../utils/strategyTransform';

const StepReview = ({ data, onSubmit, isEdit = false }) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const validation = validateStrategy(data);

  const handleSubmit = async () => {
    if (!validation.isValid) return;
    
    setSubmitting(true);
    setError(null);
    
    try {
      await onSubmit(data);
    } catch (err) {
      setError(err.message || `Failed to ${isEdit ? 'update' : 'create'} strategy`);
    } finally {
      setSubmitting(false);
    }
  };

  const conditions = data.conditions || [];
  const signals = data.expected_signals || [];

  const getConditionIcon = (type) => {
    if (!type) return InfoIcon;
    switch (type) {
      case 'simultaneous':
        return FlashIcon;
      case 'time_bound':
        return SequenceIcon;
      case 'interference':
        return InterferenceIcon;
      default:
        return CheckIcon;
    }
  };

  const getConditionColor = (type) => {
    if (!type) return { main: '#94a3b8', bg: '#f1f5f9' };
    switch (type) {
      case 'simultaneous':
        return { main: '#3b82f6', bg: '#eff6ff' };
      case 'time_bound':
        return { main: '#8b5cf6', bg: '#f5f3ff' };
      case 'interference':
        return { main: '#ef4444', bg: '#fef2f2' };
      default:
        return { main: '#667eea', bg: '#eff6ff' };
    }
  };

  const getConditionTypeLabel = (type) => {
    if (!type) return 'Not configured';
    if (type === 'time_bound') return 'Time Bound';
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const SignalChip = ({ signal }) => {
    if (!signal || !signal.algo) return null;
    const isLong = signal.direction === 'LONG';
    const DirectionIcon = isLong ? LongIcon : ShortIcon;
    const directionColor = isLong ? '#10b981' : '#ef4444';
    const directionBg = isLong ? '#d1fae5' : '#fee2e2';
    
    return (
      <Chip
        icon={<DirectionIcon sx={{ fontSize: '14px !important' }} />}
        label={`${signal.algo}:${signal.direction}`}
        size="small"
        sx={{
          bgcolor: directionBg,
          color: directionColor,
          fontWeight: 600,
          fontFamily: 'monospace',
          fontSize: '0.7rem',
          height: 22,
          '& .MuiChip-icon': {
            color: directionColor,
          },
        }}
      />
    );
  };

  const renderConditionDetails = (condition) => {
    if (condition.type === 'simultaneous') {
      return (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
          <Typography variant="caption" sx={{ color: '#64748b', mr: 0.5 }}>Signals:</Typography>
          {condition.signals?.map((signal, idx) => (
            <SignalChip key={idx} signal={signal} />
          ))}
        </Stack>
      );
    }
    
    if (condition.type === 'time_bound' || condition.type === 'interference') {
      return (
        <Stack spacing={1} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" sx={{ color: '#64748b', minWidth: 60 }}>Trigger:</Typography>
            <SignalChip signal={condition.trigger} />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" sx={{ color: '#64748b', minWidth: 60 }}>Target:</Typography>
            <SignalChip signal={condition.target} />
          </Stack>
          {condition.type === 'time_bound' && condition.timing && (
            <Typography variant="caption" sx={{ color: '#64748b', mt: 0.5 }}>
              Time Window: {condition.timing.min_minutes}m{condition.timing.min_seconds}s - {condition.timing.max_minutes}m{condition.timing.max_seconds}s
            </Typography>
          )}
        </Stack>
      );
    }
    
    return null;
  };

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CheckIcon sx={{ color: 'white', fontSize: 28 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 800, 
              color: '#1e293b',
              mb: 0.5,
            }}
          >
            Review & {isEdit ? 'Update' : 'Create'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Verify all details before {isEdit ? 'updating' : 'creating'} your strategy
          </Typography>
        </Box>
      </Stack>

      <Divider sx={{ mb: 4 }} />

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Validation Status */}
      <Card
        sx={{
          mb: 4,
          borderRadius: 3,
          border: '2px solid',
          borderColor: validation.isValid ? '#10b981' : '#ef4444',
          background: validation.isValid 
            ? 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)' 
            : 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
          boxShadow: validation.isValid
            ? '0 8px 24px rgba(16, 185, 129, 0.2)'
            : '0 8px 24px rgba(239, 68, 68, 0.2)',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '12px',
                bgcolor: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {validation.isValid ? (
                <CheckIcon sx={{ fontSize: 32, color: '#10b981' }} />
              ) : (
                <ErrorIcon sx={{ fontSize: 32, color: '#ef4444' }} />
              )}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: validation.isValid ? '#065f46' : '#991b1b' }}>
                {validation.isValid ? '✓ Ready to ' + (isEdit ? 'Update' : 'Create') : '⚠ Configuration Issues'}
              </Typography>
              <Typography variant="body2" sx={{ color: validation.isValid ? '#047857' : '#b91c1c' }}>
                {validation.isValid 
                  ? 'All validation checks passed successfully'
                  : `${validation.errors.length} issue${validation.errors.length !== 1 ? 's' : ''} require attention`
                }
              </Typography>
            </Box>
          </Stack>
          
          {!validation.isValid && (
            <Stack spacing={1} sx={{ mt: 2 }}>
              {validation.errors.map((errorMsg, idx) => (
                <Alert 
                  key={idx} 
                  severity="error" 
                  sx={{ 
                    borderRadius: 2,
                    '& .MuiAlert-icon': {
                      color: '#ef4444',
                    },
                  }}
                >
                  {errorMsg}
                </Alert>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Basic Info */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, border: '2px solid #e2e8f0', height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '10px',
                    bgcolor: '#eff6ff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <DescIcon sx={{ color: '#3b82f6', fontSize: 24 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                  Strategy Details
                </Typography>
              </Stack>
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Strategy ID
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b', mt: 0.5, fontFamily: 'monospace' }}>
                    {data.strategy_id || 'Not set'}
                  </Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Symbol & Timeframe
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                    <Chip
                      label={data.symbol || 'N/A'}
                      sx={{
                        bgcolor: '#dbeafe',
                        color: '#1e40af',
                        fontWeight: 700,
                        fontSize: '0.875rem',
                      }}
                    />
                    <Chip
                      label={data.timeframe || 'N/A'}
                      sx={{
                        bgcolor: '#f3e8ff',
                        color: '#6b21a8',
                        fontWeight: 700,
                        fontSize: '0.875rem',
                      }}
                    />
                  </Stack>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Entry Logic
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b', mt: 0.5, textTransform: 'uppercase' }}>
                    {data.entry_logic || 'ALL'}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Strategy Stats */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, border: '2px solid #e2e8f0', height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '10px',
                    bgcolor: '#f5f3ff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CheckIcon sx={{ color: '#8b5cf6', fontSize: 24 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                  Configuration Summary
                </Typography>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Paper elevation={0} sx={{ p: 2, bgcolor: '#eff6ff', borderRadius: 2, textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 800, color: '#3b82f6' }}>
                      {signals.length}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#1e40af', fontWeight: 600 }}>
                      Signal{signals.length !== 1 ? 's' : ''}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper elevation={0} sx={{ p: 2, bgcolor: '#f5f3ff', borderRadius: 2, textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 800, color: '#8b5cf6' }}>
                      {conditions.length}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#6b21a8', fontWeight: 600 }}>
                      Condition{conditions.length !== 1 ? 's' : ''}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
              <Divider sx={{ my: 2.5 }} />
              <Box>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Confirmation Condition
                </Typography>
                <Chip
                  icon={<CheckIcon sx={{ fontSize: '16px !important' }} />}
                  label={
                    conditions[data.confirmation_condition_index]?.name || 
                    `Condition ${(data.confirmation_condition_index ?? 0) + 1}` || 
                    'Not set'
                  }
                  sx={{
                    mt: 1,
                    bgcolor: '#d1fae5',
                    color: '#047857',
                    fontWeight: 700,
                    border: '2px solid #a7f3d0',
                    '& .MuiChip-icon': {
                      color: '#047857',
                    },
                  }}
                />
              </Box>
              <Divider sx={{ my: 2.5 }} />
              <Box>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Alert on Entry
                </Typography>
                <Chip
                  icon={<CheckIcon sx={{ fontSize: '16px !important' }} />}
                  label={data.alert_config?.alert_on_entry ? 'Enabled' : 'Disabled'}
                  sx={{
                    mt: 1,
                    bgcolor: data.alert_config?.alert_on_entry ? '#d1fae5' : '#fee2e2',
                    color: data.alert_config?.alert_on_entry ? '#047857' : '#991b1b',
                    fontWeight: 700,
                    border: `2px solid ${data.alert_config?.alert_on_entry ? '#a7f3d0' : '#fecaca'}`,
                    '& .MuiChip-icon': {
                      color: data.alert_config?.alert_on_entry ? '#047857' : '#991b1b',
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Signals List */}
        {signals.length > 0 && (
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 3, border: '2px solid #e2e8f0' }}>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '10px',
                      bgcolor: '#fef3c7',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <FlashIcon sx={{ color: '#f59e0b', fontSize: 24 }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                    Expected Signals ({signals.length})
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {signals.map((signal, index) => (
                    <SignalChip key={signal.id || index} signal={signal} />
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Conditions Summary */}
        {conditions.length > 0 && (
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 3, border: '2px solid #e2e8f0' }}>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '10px',
                      bgcolor: '#f5f3ff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <SequenceIcon sx={{ color: '#8b5cf6', fontSize: 24 }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                    Trigger Conditions
                  </Typography>
                </Stack>
                <Stack spacing={2}>
                  {conditions.map((condition, idx) => {
                    const Icon = getConditionIcon(condition.type);
                    const colors = getConditionColor(condition.type);
                    const isConfirmation = data.confirmation_condition_index === idx;

                    return (
                      <Paper
                        key={condition.id || idx}
                        elevation={0}
                        sx={{
                          p: 2.5,
                          borderRadius: 2,
                          bgcolor: isConfirmation ? alpha('#10b981', 0.08) : alpha(colors.main, 0.08),
                          border: '2px solid',
                          borderColor: isConfirmation ? '#10b981' : colors.main,
                        }}
                      >
                        <Stack spacing={1}>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '10px',
                                bgcolor: colors.bg,
                                border: `2px solid ${colors.main}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Icon sx={{ color: colors.main, fontSize: 22 }} />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Typography sx={{ fontWeight: 700, color: '#1e293b' }}>
                                  {condition.name || `Condition ${idx + 1}`}
                                </Typography>
                                <Chip
                                  label={getConditionTypeLabel(condition.type)}
                                  size="small"
                                  sx={{
                                    bgcolor: colors.bg,
                                    color: colors.main,
                                    fontWeight: 700,
                                    fontSize: '0.7rem',
                                    height: 22,
                                  }}
                                />
                                {isConfirmation && (
                                  <Chip
                                    icon={<CheckIcon sx={{ fontSize: '14px !important' }} />}
                                    label="CONFIRMATION"
                                    size="small"
                                    sx={{
                                      bgcolor: '#d1fae5',
                                      color: '#047857',
                                      fontWeight: 700,
                                      fontSize: '0.7rem',
                                      height: 22,
                                      '& .MuiChip-icon': {
                                        color: '#047857',
                                      },
                                    }}
                                  />
                                )}
                              </Stack>
                            </Box>
                          </Stack>
                          {renderConditionDetails(condition)}
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Submit Button */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Button
          variant="contained"
          size="large"
          disabled={!validation.isValid || submitting}
          onClick={handleSubmit}
          sx={{
            px: 6,
            py: 2,
            borderRadius: 2,
            background: validation.isValid
              ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              : '#cbd5e1',
            fontSize: '1.1rem',
            fontWeight: 700,
            textTransform: 'none',
            boxShadow: validation.isValid
              ? '0 8px 24px rgba(102, 126, 234, 0.25)'
              : 'none',
            '&:hover': {
              background: validation.isValid
                ? 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
                : '#cbd5e1',
              boxShadow: validation.isValid
                ? '0 12px 32px rgba(102, 126, 234, 0.35)'
                : 'none',
              transform: validation.isValid ? 'translateY(-2px)' : 'none',
            },
            '&:disabled': {
              color: 'white',
            },
            transition: 'all 0.2s',
          }}
        >
          {submitting ? (
            <Stack direction="row" spacing={2} alignItems="center">
              <CircularProgress size={20} sx={{ color: '#fff' }} />
              <span>{isEdit ? 'Updating' : 'Creating'} Strategy...</span>
            </Stack>
          ) : (
            `${isEdit ? 'Update' : 'Create'} Strategy`
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default StepReview;
